import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventEmitter } from './useEventEmitter';
import { useSnackbar } from '@/contexts/SnackbarContext';

type SelectionMenuState = {
  visible: boolean;
  text: string;
  rect: DOMRect | null;
};

type Options = {
  container?: React.RefObject<HTMLElement>;
  offset?: number;
  selectable?: boolean; // 是否允许选中，默认为 true
};

export function useSelectionMenu(options: Options = {}) {
  const { container, offset = 8, selectable = true } = options;
  const { showSuccess } = useSnackbar();

  const [state, setState] = useState<SelectionMenuState>({
    visible: false,
    text: '',
    rect: null,
  });

  const emitter = useEventEmitter();
  const lastRangeRef = useRef<Range | null>(null);

  const hide = useCallback(() => {
    setState({ visible: false, text: '', rect: null });
  }, []);

  const update = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      hide();
      return;
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      hide();
      return;
    }

    // 限制在容器内
    if (container?.current) {
      const ancestor = range.commonAncestorContainer;
      if (ancestor instanceof Node && !container.current.contains(ancestor)) {
        hide();
        return;
      }
    }

    const rect = range.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) {
      hide();
      return;
    }

    lastRangeRef.current = range;

    setState({
      visible: true,
      text: selection.toString(),
      rect,
    });
  }, [container, hide]);

  useEffect(() => {
    if (selectable) {
      document.addEventListener('selectionchange', update);
      document.addEventListener('mouseup', update);
      window.addEventListener('scroll', hide, { passive: true });
    }

    return () => {
      document.removeEventListener('selectionchange', update);
      document.removeEventListener('mouseup', update);
      window.removeEventListener('scroll', hide);
    };
  }, [update, hide, selectable]);

  const copy = useCallback(async () => {
    if (!state.text) return;
    await navigator.clipboard.writeText(state.text);
    showSuccess('Copied to clipboard');
    window.getSelection()?.removeAllRanges();
    hide();
  }, [state.text, hide, showSuccess]);

  const addToChat = useCallback(() => {
    emitter.emit('message-preset', { action: 'quote', content: state.text });
    window.getSelection()?.removeAllRanges();
    hide();
  }, [emitter, state.text, hide]);

  const splitContent = useCallback(() => {
    if (!container?.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    // 获取整个容器的文本内容
    const fullText = container.current.innerText;

    // 获取选中的文本
    const selectedText = selection.toString();

    // 创建一个从容器开始到选择起点的范围，计算精确的字符偏移
    const startRange = document.createRange();
    startRange.selectNodeContents(container.current);
    startRange.setEnd(range.startContainer, range.startOffset);

    // 获取起点之前的文本长度
    const beforeText = startRange.toString();
    const startOffset = beforeText.length;
    hide();
    selection.removeAllRanges();
    return {
      beforeText: fullText.slice(0, startOffset),
      selectedText: selectedText,
      afterText: fullText.slice(startOffset + selectedText.length),
    };
  }, [container, hide]);

  return {
    visible: state.visible,
    text: state.text,
    rect: state.rect,
    copy,
    addToChat,
    splitContent,
    hide,
    offset,
  };
}
